import { useAppNavigation } from '@euler/app/components/AppNavigationProvider';
import { useCurrentUserDangerously } from '@euler/app/flows/auth/context';
import { useServiceRecordContext } from '@euler/app/flows/record/ServiceRecordContext';
import { reportTabKeyFromScreen } from '@euler/app/flows/report/functions/util';
import { ReportNavParams } from '@euler/app/flows/report/ReportScreens';
import { TaskTimelineCell } from '@euler/app/flows/task/components/TaskTimeline';
import {
  buildTimeline,
  useTaskTimelineModels,
} from '@euler/app/flows/task/functions/timeline';
import {
  EventInfo,
  TimelineSectionType,
} from '@euler/app/flows/task/functions/timeline/types';
import { EmptyView } from '@euler/components/EmptyView';
import { useShareReport } from '@euler/functions/sharing/useShareReport';
import { useContainerWidth } from '@euler/functions/useAdaptiveContainerWidth';
import { VehicleServiceEvent } from '@euler/model';
import { dateFromValue } from '@euler/utils/datetime';
import { usePersistFn } from '@euler/utils/hooks';
import { memo, useMemo } from 'react';
import {
  FlatList,
  Platform,
  ScrollView,
  StyleProp,
  ViewStyle,
} from 'react-native';

export const WiredServiceRecordEventList = memo(
  ({ style }: { style?: StyleProp<ViewStyle> }) => {
    const navigation = useAppNavigation();
    const width = useContainerWidth();
    const profile = useCurrentUserDangerously();
    const { serviceRecord, reportTarget } = useServiceRecordContext();

    const events = useMemo(
      () => serviceRecord.events?.map(toEventInfo) ?? [],
      [serviceRecord.events],
    );

    const sections = useMemo(
      () =>
        buildTimeline(events ?? [], {
          isIndependentDeliveryCheckEnabled:
            profile.store.isIndependentDeliveryCheckEnabled,
        }),
      [events, profile.store.isIndependentDeliveryCheckEnabled],
    );

    const { shareReportViaWeixin } = useShareReport();

    const viewReport = usePersistFn((type: keyof ReportNavParams) => {
      navigation.navigate(type, {
        target: reportTarget,
        standalone: true,
        onShare: () => {
          void shareReportViaWeixin({
            target: reportTarget,
            orderNo: reportTarget.orderNo,
            reportNo: reportTarget.reportNo,
            licensePlateNo: reportTarget.licensePlateNo,
            tab: reportTabKeyFromScreen(type),
          });
        },
      });
    });

    const onPressSectionHeader = usePersistFn(
      (section: TimelineSectionType) => {
        switch (section) {
          case 'order': {
            break;
          }
          case 'quotation':
          case 'none':
            break;
          case 'pre-inspection':
            viewReport('preInspectionReport');
            break;
          case 'inspection':
            viewReport('inspectionReport');
            break;
          case 'construction':
            viewReport('constructionReport');
            break;
          case 'delivery-check':
            viewReport('deliveryCheckReport');
            break;
          default:
            break;
        }
      },
    );

    const { cells } = useTaskTimelineModels({ sections, onPressSectionHeader });

    if (!events?.length) {
      return (
        <ScrollView
          css={`
            flex: 1;
            background-color: #fff;
          `}
          contentContainerStyle={[
            {
              flex: 1,
              alignItems: 'center',
              justifyContent: 'center',
            },
            style,
          ]}
        >
          <EmptyView message="暂无相关工作记录" />
        </ScrollView>
      );
    }

    return (
      <FlatList
        data={cells}
        keyExtractor={x => x.id}
        initialNumToRender={20}
        showsVerticalScrollIndicator={Platform.OS === 'web'}
        contentContainerStyle={[{ backgroundColor: '#fff' }, style]}
        renderItem={({ item }) => (
          <TaskTimelineCell model={item} width={width} />
        )}
      />
    );
  },
);

function toEventInfo(event: VehicleServiceEvent): EventInfo {
  return {
    id: event.id,
    type: event.type,
    subType: event.subType,
    timestamp: dateFromValue(event.timestamp),
    data: event.data,
    dataType: event.dataType,
    dataVersion: event.dataVersion,
    author: event.author,
  };
}
